import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { api, apiErrorMessage } from "../lib/api";
import { toast } from "sonner";
import { TID } from "../lib/testIds";
import { PlayCircle, CheckCircle2, XCircle } from "lucide-react";

const PRESETS = [
  {
    label: "Gemini Web",
    url: "https://gemini.google.com/app",
    selector: "textarea, [contenteditable='true']",
  },
  {
    label: "ChatGPT",
    url: "https://chat.openai.com/",
    selector: "textarea",
  },
];

export default function VerifySessionDialog({
  open,
  onOpenChange,
  profileName,
}) {
  const [url, setUrl] = useState(PRESETS[0].url);
  const [selector, setSelector] = useState(PRESETS[0].selector);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  function applyPreset(p) {
    setUrl(p.url);
    setSelector(p.selector);
    setResult(null);
  }

  async function submit(e) {
    e.preventDefault();
    setRunning(true);
    setResult(null);
    try {
      const { data } = await api.post(
        `/browser/profiles/${encodeURIComponent(profileName)}/verify`,
        { url, logged_in_selector: selector, timeout_ms: 20000 }
      );
      setResult(data);
    } catch (err) {
      setResult({ error: apiErrorMessage(err) });
      toast.error(apiErrorMessage(err));
    } finally {
      setRunning(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={TID.browser.verifyDialog}
        className="bg-zinc-950 border border-zinc-800 text-zinc-100 sm:max-w-[560px]"
      >
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <PlayCircle className="h-4 w-4" />
            Verificar sesión — {profileName}
          </DialogTitle>
          <DialogDescription className="text-zinc-500">
            Abre la URL con el storage_state cargado y comprueba que el selector
            existe. Todo se ejecuta en Chromium remoto vía KRAI API.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap gap-2">
          {PRESETS.map((p) => (
            <button
              key={p.label}
              type="button"
              onClick={() => applyPreset(p)}
              className="text-xs rounded-full border border-zinc-800 bg-zinc-900 hover:bg-zinc-800 text-zinc-300 px-3 py-1"
            >
              {p.label}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label className="text-zinc-300">URL</Label>
            <Input
              data-testid={TID.browser.verifyUrl}
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className="bg-zinc-900 border-zinc-800 font-mono text-sm"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-zinc-300">Selector que confirma login</Label>
            <Input
              data-testid={TID.browser.verifySelector}
              value={selector}
              onChange={(e) => setSelector(e.target.value)}
              className="bg-zinc-900 border-zinc-800 font-mono text-sm"
            />
          </div>

          {result && (
            <div
              data-testid={TID.browser.verifyResult}
              className="rounded-md border border-zinc-800 bg-black/40 p-3 text-xs text-zinc-300 font-mono space-y-1"
            >
              {result.error ? (
                <div className="flex items-center gap-2 text-red-400">
                  <XCircle className="h-3.5 w-3.5" />
                  {result.error}
                </div>
              ) : (() => {
                // Accept both response shapes so verification is robust
                // across KRAI API implementations:
                //   - reference: {logged_in: true, page_title, duration_ms}
                //   - alt:       {ok: true, code: "verified", message, ...}
                const isValid =
                  result.logged_in === true ||
                  (result.ok === true &&
                    (result.code === "verified" || result.code === "ok" || result.code == null));
                return (
                  <>
                    <div className="flex items-center gap-2">
                      {isValid ? (
                        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />
                      ) : (
                        <XCircle className="h-3.5 w-3.5 text-red-400" />
                      )}
                      <span className={isValid ? "text-emerald-400" : "text-red-400"}>
                        {isValid ? "Sesión válida" : "No autenticado"}
                      </span>
                    </div>
                    {result.message && <div>msg: {result.message}</div>}
                    {result.page_title && <div>title: {result.page_title}</div>}
                    {typeof result.duration_ms === "number" && (
                      <div>duración: {result.duration_ms} ms</div>
                    )}
                  </>
                );
              })()}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={() => onOpenChange(false)}
              className="bg-zinc-900 hover:bg-zinc-800 border border-zinc-800"
            >
              Cerrar
            </Button>
            <Button
              type="submit"
              data-testid={TID.browser.verifySubmit}
              disabled={running || !url || !selector}
              className="bg-white text-black hover:bg-zinc-200"
            >
              {running ? "Verificando…" : "Verificar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
